import React, { useState } from 'react';
// @ts-ignore
import styles from './MedicalReportsList.module.scss';
import { useAppContext } from '../../Services/AppContext';
import MedicalReportDetailLatam from './MedicalReportDetailLatam';
import MedicalReportDetailDefault from './MedicalReportDetailDefault';
import apiClient from '../../Services/ApiClient';
import useNotification from '../../Hooks/useNotification.hook';
import useTranslation from '../../Hooks/useTranslation';
import withOverlayLoading from '../../Libs/withOverlayLoading';
import { useEffectAsync } from '../../helpers';
import { useLazyRootWorkplaceByChildQuery } from '../../graphql/graphql';
import { Partner } from '../../Enums/Partner.enum';
import MedicalReportDetailMou from './MedicalReportDetailMou';
import { useDiscourseContext } from '../../Modules/Discourse/Services/DiscourseContext';
import MedicalRequestCreateModal from '../../Modules/MedicalRequests/Components/MedicalRequestCreate/MedicalRequestCreateModal';
import MedicalReportDetailSerbia from './Serbia/MedicalReportDetailSerbia';
import MedicalReportDetailPoland from './Poland/MedicalReportDetailPoland';

interface MedicalReportDetailWrapperProps {
	patientId: string;
	workplaceId: string;
	loggedUserId: string;
	caseId?: string;
	onSubmit?: () => void;
	defaultData?: any;
	customSuccess?: (values: any) => void;
	onMedicalReportCreate?: () => void;
	setLoading: (newState: boolean) => void;
	attachToDiscourse?: boolean;
	disableSubmit?: boolean;
	createDekurz?: boolean;
}

const MedicalReportDetailWrapper: React.FC<MedicalReportDetailWrapperProps> = ({
	patientId,
	workplaceId,
	loggedUserId,
	defaultData,
	caseId,
	onSubmit,
	customSuccess,
	setLoading,
	onMedicalReportCreate,
	attachToDiscourse = false,
	disableSubmit,
	createDekurz = true,
}) => {
	const { globalTemplate } = useAppContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [getRootWorkplace] = useLazyRootWorkplaceByChildQuery();
	const [initialized, setInitialized] = useState(false);
	const [type, setType] = useState('default');
	const { handleAttachToDiscourse } = useDiscourseContext();

	const [isMedicalRequestCreateOpen, setIsMedicalRequestCreateOpen] =
		useState(false);
	const [createdDocumentData, setCreatedDocumentData] = useState<
		{ doctorId?: string; patientId?: string } | undefined
	>(undefined);

	useEffectAsync(async () => {
		const { workplace } = await getRootWorkplace({
			workplaceId,
		}).unwrap();
		if (workplace?.rootWorkplace?.partnerId === Partner.MOU_MEDDI) {
			setType('mou');
		}
		setInitialized(true);
	});

	const runFinalSteps = async (doctorId?: string, patientId?: string) => {
		if (attachToDiscourse && handleAttachToDiscourse) {
			// attach doctor (only latam)
			if (doctorId) {
				await handleAttachToDiscourse(doctorId, 'medicalReport');
			}

			// attach patient
			if (patientId) {
				await handleAttachToDiscourse(patientId, 'medicalReportPatient');
			}
		}

		onSubmit?.();
		onMedicalReportCreate?.();
	};

	const handleSaveMedicalReport = async (values: any) => {
		setLoading(true);
		try {
			const { data: document } = await apiClient.post(
				'/user/' + patientId + '/medical-report',
				{
					data: {
						rawData: values,
						caseId: caseId,
					},
					createDekurz,
				},
			);

			if (document) {
				const doctorId = document.doctor?.id;
				const patientId = document.patient?.id;

				setCreatedDocumentData({
					doctorId,
					patientId,
				});

				showNotificationSuccess({
					content: t('base.medicalReportSaved'),
				});

				if (values.contactSpecialist) {
					setIsMedicalRequestCreateOpen(true);
				} else {
					await runFinalSteps(doctorId, patientId);
				}
			}
		} catch (e: any) {
			if (e.response?.data?.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}
		setLoading(false);
	};

	const handleAfterCreateMedicalRequest = async () => {
		setIsMedicalRequestCreateOpen(false);
		await runFinalSteps(
			createdDocumentData?.doctorId,
			createdDocumentData?.patientId,
		);
	};

	let component;

	if (!initialized) {
		return null;
	}

	switch (globalTemplate) {
		case 'peru':
		case 'ecuador':
		case 'mexico':
		case 'colombia':
			component = (
				<MedicalReportDetailLatam
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={handleSaveMedicalReport}
					customSuccess={customSuccess}
					disableSubmit={disableSubmit}
				/>
			);
			break;
		case 'serbia':
			component = (
				<MedicalReportDetailSerbia
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={customSuccess ? customSuccess : handleSaveMedicalReport}
					disableSubmit={disableSubmit}
				/>
			);
			break;
		case 'poland':
			component = (
				<MedicalReportDetailPoland
					patientId={patientId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
					defaultData={defaultData}
					onSuccess={customSuccess ? customSuccess : handleSaveMedicalReport}
					disableSubmit={disableSubmit}
				/>
			);
			break;
		default:
			if (type === 'mou') {
				component = (
					<MedicalReportDetailMou
						patientId={patientId}
						workplaceId={workplaceId}
						loggedUserId={loggedUserId}
						defaultData={defaultData}
						onSuccess={handleSaveMedicalReport}
						customSuccess={customSuccess}
					/>
				);
			} else {
				component = (
					<>
						<MedicalReportDetailDefault
							patientId={patientId}
							workplaceId={workplaceId}
							loggedUserId={loggedUserId}
							defaultData={defaultData}
							caseId={caseId}
							onSuccess={handleSaveMedicalReport}
							customSuccess={customSuccess}
						/>
						{isMedicalRequestCreateOpen && (
							<MedicalRequestCreateModal
								isOpen
								setIsOpen={handleAfterCreateMedicalRequest}
								patientId={patientId}
								workplaceId={workplaceId}
								onCreate={handleAfterCreateMedicalRequest}
								autoOpenByKey='medicalRequestFormK'
								caseId={caseId}
							/>
						)}
					</>
				);
			}
			break;
	}

	return component;
};

export default withOverlayLoading(MedicalReportDetailWrapper);
